import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type {
  Afiliado,
  AfiliadoCreatePayload,
  AfiliadoUpdatePayload,
  AfiliadosFilter,
  AfiliadosResult,
} from '../types/afiliado';
import { cuitDigits } from '../utils/format';

const QK = {
  all: ['afiliados'] as const,
  list: (filter: AfiliadosFilter) => ['afiliados', 'list', filter] as const,
  detail: (id: string) => ['afiliados', 'detail', id] as const,
};

const DEFAULT_PAGE_SIZE = 25;

// ----- LIST -----

async function fetchAfiliados(filter: AfiliadosFilter): Promise<AfiliadosResult> {
  const page = filter.page ?? 0;
  const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;
  const from = page * pageSize;
  const to = from + pageSize - 1;

  // Contexto de empresa (admin_empresa o admin impersonando): se acota a los
  // afiliados con relacion laboral ACTIVA con esa empresa. El join embebido
  // !inner filtra sin duplicar la fila del afiliado (los hijos se anidan), asi
  // el count:exact sigue contando afiliados, no relaciones (#494).
  // Sin empresaId (admin/operador): padron completo.
  const selectCols = filter.empresaId
    ? '*, relaciones_laborales!inner(empresa_id, estado)'
    : '*';

  let query = supabase
    .from('afiliados')
    .select(selectCols, { count: 'exact' })
    .order('apellido', { ascending: true })
    .order('nombre', { ascending: true })
    .range(from, to);

  if (filter.empresaId) {
    query = query
      .eq('relaciones_laborales.empresa_id', filter.empresaId)
      .eq('relaciones_laborales.estado', 'activa');
  }

  if (filter.estado && filter.estado !== 'todos') {
    query = query.eq('estado', filter.estado);
  }

  if (filter.search && filter.search.trim()) {
    const term = filter.search.trim();
    // Buscar por apellido O nombre O CUIL (sin guiones) O numero documento
    const digits = cuitDigits(term);
    if (digits.length >= 2) {
      query = query.or(
        `apellido.ilike.%${term}%,nombre.ilike.%${term}%,cuil.ilike.%${digits}%,numero_documento.ilike.%${digits}%`
      );
    } else {
      query = query.or(`apellido.ilike.%${term}%,nombre.ilike.%${term}%`);
    }
  }

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    // Cast via unknown: con el select dinamico supabase-js no puede inferir el
    // tipo estaticamente (el embed !inner agrega la relacion, que no usamos).
    data: (data ?? []) as unknown as Afiliado[],
    count: count ?? 0,
    page,
    pageSize,
  };
}

export function useAfiliados(filter: AfiliadosFilter = {}) {
  return useQuery({
    queryKey: QK.list(filter),
    queryFn: () => fetchAfiliados(filter),
    placeholderData: (prev) => prev,
  });
}

// ----- DETAIL -----

async function fetchAfiliado(id: string, empresaId?: string | null): Promise<Afiliado | null> {
  // En contexto de empresa se exige relacion activa con esa empresa; si el
  // afiliado no la tiene, la query no devuelve fila (maybeSingle -> null) y el
  // detalle queda inaccesible (CA-02 del #494). El login real ya lo bloquea la
  // RLS; esto ademas cubre al admin impersonando (que bypassa RLS).
  const selectCols = empresaId
    ? '*, relaciones_laborales!inner(empresa_id, estado)'
    : '*';

  let query = supabase.from('afiliados').select(selectCols).eq('id', id);
  if (empresaId) {
    query = query
      .eq('relaciones_laborales.empresa_id', empresaId)
      .eq('relaciones_laborales.estado', 'activa');
  }

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return (data as unknown as Afiliado) ?? null;
}

export function useAfiliado(id: string | undefined, empresaId?: string | null) {
  return useQuery({
    queryKey: id ? [...QK.detail(id), empresaId ?? 'all'] : ['afiliados', 'detail', 'none'],
    queryFn: () => fetchAfiliado(id!, empresaId),
    enabled: !!id,
  });
}

// ----- CREATE -----

async function createAfiliado(payload: AfiliadoCreatePayload): Promise<Afiliado> {
  const normalized = {
    ...payload,
    cuil: cuitDigits(payload.cuil),
    numero_documento: payload.numero_documento.trim(),
  };
  const { data, error } = await supabase
    .from('afiliados')
    .insert(normalized)
    .select('*')
    .single();
  if (error) throw error;
  return data as Afiliado;
}

export function useCreateAfiliado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createAfiliado,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}

// ----- CREATE (rol empresa, via RPC) -----

export interface CrearAfiliadoEmpresaResult {
  afiliado_id: string;
  /** true si el CUIL ya existia y se vinculo a la empresa (no se creo uno nuevo). */
  vinculado: boolean;
}

/**
 * Alta de afiliado para el rol empresa (#495). Va por el RPC
 * `crear_afiliado_empresa`, que autoriza en el backend, resuelve el caso de
 * CUIL existente (vincula) y crea la relacion laboral minima con la empresa
 * del usuario. La empresa NO elige la empresa: sale de su contexto.
 */
async function crearAfiliadoEmpresa(
  payload: AfiliadoCreatePayload,
): Promise<CrearAfiliadoEmpresaResult> {
  const p_datos = { ...payload, cuil: cuitDigits(payload.cuil) };
  const { data, error } = await supabase.rpc('crear_afiliado_empresa', { p_datos });
  if (error) throw error;
  return data as CrearAfiliadoEmpresaResult;
}

export function useCrearAfiliadoEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: crearAfiliadoEmpresa,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}

// ----- UPDATE -----

async function updateAfiliado({
  id,
  payload,
}: {
  id: string;
  payload: AfiliadoUpdatePayload;
}): Promise<Afiliado> {
  const normalized = { ...payload };
  if (normalized.cuil) normalized.cuil = cuitDigits(normalized.cuil);
  if (normalized.numero_documento) normalized.numero_documento = normalized.numero_documento.trim();

  const { data, error } = await supabase
    .from('afiliados')
    .update(normalized)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Afiliado;
}

export function useUpdateAfiliado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateAfiliado,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.setQueryData(QK.detail(data.id), data);
    },
  });
}
